import { useEffect, useRef, useState } from "react";
import { trackTime } from "../api/tracking";
import { useUserContext } from "../atlas/contexts/User/UserContextProvider";
import { isGuestAccessMode } from "../utils/guest";

const DURATION = 5000;
const SEND_INTERVAL = 30000;
const COOKIE_NAME = "timeTracker";

export const useTrackTime = ({
  component = "Lesson",
  lessonId,
}: {
  component?: string;
  lessonId?: number | null;
}) => {
  const { isAuthenticated } = useUserContext();
  // Check if another visible window is tracking time
  const flagTrackingRef = useRef<number | null>(null);

  // Ensure only one window is tracking time at a time
  // A user might have multiple windows visible at a time
  const exclusiveWindowTracking = () => {
    const cookieValue = Number(localStorage.getItem(COOKIE_NAME));
    const currentTime = Date.now();

    // If we set the cookie, the cookie is unset,
    // or the cookie was set a long time ago, we can track.
    if (
      (flagTrackingRef.current && cookieValue == flagTrackingRef.current) ||
      !cookieValue ||
      cookieValue < currentTime - DURATION * 2
    ) {
      localStorage.setItem(COOKIE_NAME, currentTime.toString());
      flagTrackingRef.current = currentTime;
      return true;
    }

    // Otherwise, another window set the cookie. We can't track.
    return false;
  };

  // Remove the tracking cookie
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (exclusiveWindowTracking()) {
        localStorage.removeItem(COOKIE_NAME);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Flag for if the user is on the current tab
  const [visible, setVisible] = useState(document.visibilityState == "visible");

  // Keeps visible up to date
  useEffect(() => {
    const updateVisible = () => setVisible(document.visibilityState == "visible");
    document.addEventListener("visibilitychange", updateVisible);
    return () => document.removeEventListener("visibilitychange", updateVisible);
  }, []);

  // The actual time tracking
  useEffect(() => {
    // Guests have no time-tracker endpoint (the guest-mode client rewrites
    // /internal_api -> /scorm, which has no time_tracker route)
    if (visible && lessonId && !isGuestAccessMode()) {
      // Start a timer to periodically send time updates
      let timer = 0;
      const timerInterval = setInterval(() => {
        if (exclusiveWindowTracking()) {
          timer += DURATION;
        }
      }, DURATION);

      const sendInterval = setInterval(() => {
        const duration = timer;
        timer = 0;

        trackTime({ component, duration, parameters: { moduleId: lessonId } });
      }, SEND_INTERVAL);
      return () => {
        clearInterval(timerInterval);
        clearInterval(sendInterval);
        isAuthenticated &&
          trackTime({
            component,
            duration: timer,
            parameters: { moduleId: lessonId },
          });
      };
    }
  }, [visible, isAuthenticated, lessonId]);
};
